import commonActions from "../common/actions";
import authActions from "./actions";
import axios from "axios";
import { takeEvery, call, all, put, delay, select } from "redux-saga/effects";
import { API_URL } from "../../utils/constants";
import Cookies from "js-cookie";
import setAuthToken from "../../utils/setAuthToken";
import rptAxios, { setRptAuthToken } from "../../utils/rptAxios";
import {
  handleDecodeJSON,
  handleEncodeJson,
} from "../../utils/encodeDecodeJson";
import examActions from "../exam/actions";
import { getLocation, reconcileSectionsToWindow } from "../../utils/commonFunctions";
import { PROCTORING_TYPE } from "../../config/appConfig";
import { CodeOffOutlined } from "@mui/icons-material";
import { handleError } from "../../utils/errorUtils";
const { ipcRenderer } = window.require
  ? window.require("electron")
  : { ipcRenderer: null };

const authSagas = function* () {
  yield all([yield takeEvery(authActions.CANDIDATE_LOGIN, candidateLogin)]); // early start exam
  yield all([yield takeEvery(authActions.RPT_CANDIDATE_LOGIN, rptCandidateLogin)]);  // rpt login
  yield all([ yield takeEvery(authActions.CANDIDATE_USER_LOGIN,candidateUserLogin)]); // global login
  yield all([ yield takeEvery(authActions.CANDIDATE_USER_SSO_LOGIN,candidateUserSsoLogin)]); // global sso login
};

const rptCandidateLogin = function* (payload) {
  try { 
    const result = yield call(() =>
      rptAxios.post(`/auth/candidateLogin`, payload?.data)
    );
    const candidateInfo = { ...result.data, configuration: payload?.data?.configuration };
    sessionStorage.setItem("candidateInfo", JSON.stringify(candidateInfo));

    if (result?.data?.accessToken) {
      sessionStorage.setItem("rps_token", result.data.accessToken);
      setRptAuthToken(result.data.accessToken);
    }
    if (
      payload?.data?.candidateAuthorizationEnabled === true
    ) {
      payload.data.navigate('/capture-identity');
    }
  } catch (err) {
    console.log(err)
  }
}
 

// ===== Shared post-login success/error handling for candidateUserLogin & candidateUserSsoLogin =====
const handleCandidateUserLoginSuccess = function* ({ result, mockMode, navigate, isSso }) {
  const decodedResult = yield call(() =>
    handleDecodeJSON({ decodeJSON: result?.data?.data })
  );
  if (result?.data?.code === "OK") {

    if (decodedResult?.token?.accessToken) {
      sessionStorage.setItem("loginSessionId", decodedResult.token.accessToken);
    }

    // SSO login has no login form, so backfill userName/password (used later by the
    // proctoring RPT login) from the decrypted SSO response instead.
    if (isSso && decodedResult?.user?.username) {
      sessionStorage.setItem("userName", decodedResult.user.username);
      sessionStorage.setItem("password", decodedResult.user.password);
    }

    sessionStorage.setItem(
      "examEndTime",
      decodedResult?.configuration?.endTime
    );
    // ===== Forced logout + Encoding logic KEPT =====
    const updatedEncodedResult = { ...decodedResult };
    yield call(() =>
      handleEncodeJson({ encodeJSON: updatedEncodedResult })
    );
    Cookies.set("test_engine_token", decodedResult?.token?.accessToken, {
      expires: 7,
    });
    setAuthToken(decodedResult?.token?.accessToken);
    sessionStorage.setItem(
      "candidate_details",
      JSON.stringify(decodedResult?.user || decodedResult?.candidate)
    );
    // ===== Navigation =====
    if (mockMode) {
      navigate(`/exam-schedules`);
      window.location.reload();
    }
    else {
        navigate('/exam-schedules');
    }
    // ===== Fullscreen =====
    document.documentElement.requestFullscreen?.()?.catch(() => {});
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: "success",
        show: true,
        message: "Login Successfully",
      },
    });
  } else {
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: "failed",
        show: true,
        message: "Username or Password Wrong",
      },
    });
  }
  yield delay(2000);
  yield put({
    type: commonActions.SET_ALERT,
    payload: { status: null, show: false, message: null },
  });
};

const handleCandidateUserLoginError = function* ({ err, setError }) {
  if (err?.response?.data?.code === "E_NOT_FOUND") {
    setError?.("password", {
      type: "manual",
      message: "Username or Password Wrong",
    });
  }
  yield put({
    type: commonActions.SET_ALERT,
    payload: {
      status: "failed",
      show: true,
      message: err?.response?.data?.message,
    },
  });
  yield delay(2000);
  yield put({
    type: commonActions.SET_ALERT,
    payload: { status: null, show: false, message: null },
  });
  yield put({
    type: commonActions.SET_BUTTON_LOADER,
    payload: false,
  });
  console.log(err);
};

const candidateUserLogin = function* (data) {
  const { payload } = data;
  const { navigate, setError, mockMode, url } = payload;
  try {
    let result;
    if (mockMode) {
      result = yield call(() =>
        axios.post(
          `${url || API_URL}/api/exam/mock-candidate-user-login`,
          payload.data
        )
      );
    } else {
      result = yield call(() =>
        axios.post(`${API_URL}/api/exam/candidate-user-login`, payload.data)
      );
    }
    yield call(handleCandidateUserLoginSuccess, { result, mockMode, navigate });
  }
  catch (err) {
    yield call(handleCandidateUserLoginError, { err, setError });
  }
  finally{
      yield put({
      type: commonActions.SET_BUTTON_LOADER,
      payload: false,
    });

  }
};

const candidateUserSsoLogin = function* (data) {
  const { payload } = data;
  const { navigate, setError, mockMode, onUnauthorized, onActiveSessionRunning, onBadRequest } = payload;
  try {
    const result = yield call(() =>
      axios.post(`${API_URL}/api/exam/candidate-user-sso-login`, payload.data)
    );
    yield call(handleCandidateUserLoginSuccess, { result, mockMode, navigate, isSso: true });
  } catch (err) {
    console.log(err, 'err');
    const status = err?.response?.status;
    const code = err?.response?.data?.code;
    const message = err?.response?.data?.message;

    const handled = handleError({status, code, message, onUnauthorized, onActiveSessionRunning, onBadRequest});

    if (handled) {
      return;
    }

    yield call(handleCandidateUserLoginError, { err, setError });
  }
  finally{
      yield put({
      type: commonActions.SET_BUTTON_LOADER,
      payload: false,
    });

  }
};

const candidateLogin = function* (data) {
  const { payload } = data;
  const { navigate, setError, mockMode, url } = payload;  
  try {
    let result;
    const encodeData = yield call(() =>
      handleEncodeJson({
        encodeJSON: {
          ...payload?.data,
          loginSessionId: sessionStorage.getItem("loginSessionId"),
        },
      })
    );
    if (mockMode) {
      result = yield call(() =>
        axios.post(
          `${url || API_URL}/api/exam/mock-candidate-login`,
          encodeData
        )
      );
    } else {
      result = yield call(() =>
        axios.post(`${API_URL}/api/exam/candidate-login`, encodeData)
      );
    }

    const encodedResult = JSON.stringify(result?.data?.data);
    const decodedResult = yield call(() =>
      handleDecodeJSON({ decodeJSON: result?.data?.data })
    );

    if (result?.data?.code === "OK") {
      Cookies.set("test_engine_token", decodedResult?.token?.accessToken, {
          expires: 7,
        });
      setAuthToken(decodedResult?.token?.accessToken);
      const heartBeat = yield select((state) => state.examReducer.heartBeat);
      const data = {
        auditLog: [
          {
            RegistrationNumber: `${decodedResult?.user?.id}`,
            RollNumber: `${decodedResult?.user?.rollNo}`,
            Activity: "User Logged in",
            // Timestamp: `${heartBeat?.data}`,
            IPaddress: "",
          },
        ],
      };
      yield put({ type: examActions.AUDIT_LOG, payload: { data: data } });

      // if proctoring is enable
      
      // ================= STEP-2: Get location =================
          let location = {};
          let isLocationOn = sessionStorage.getItem("isLocationOn");
          
          if(isLocationOn == "1") {
            location = yield call(getLocation);
          }

          // ================= STEP-2: RPT Login with location =======
            const proctoringEnabled = decodedResult?.configuration?.proctoringEnabled === true;
            const proctoringType = decodedResult?.configuration?.proctoringType;
            const candidateAuthorizationEnabled = decodedResult?.configuration?.candidateAuthorizationEnabled === true;
          if (proctoringEnabled) {
            yield put({
              type: authActions.RPT_CANDIDATE_LOGIN,
              data: {
                identitynumber: sessionStorage.getItem("userName"),
                password: sessionStorage.getItem("password"),
                rpsExamId: decodedResult?.rpsExamId,
                latitude: location?.latitude || null,
                longitude: location?.longitude || null,
                accuracy: location?.accuracy || null,
                proctoringType: proctoringType,
                candidateAuthorizationEnabled: candidateAuthorizationEnabled,
                configuration: { proctoringType: proctoringType },
                navigate:navigate,
              },
            });
          }


      sessionStorage.setItem(
        "examEndTime",
        decodedResult?.configuration?.endTime
      );
      const venue = sessionStorage.getItem("venue");
      if (venue && ipcRenderer) {
        try {
          ipcRenderer.send("run-python-script");
        } catch (err) {
          console.log(err);
        }
      }
      if (!decodedResult?.configuration?.forcedLogout && !mockMode) {
        const adjustedCurrentTimestamp = new Date(
          Date.now() + (5 * 60 + 30) * 60 * 1000
        );
        const formattedCurrentTimestamp = adjustedCurrentTimestamp
          .toISOString()
          .replace(/\.\d{3}/, "");
        // startTime/endTime used to be recomputed here from
        // configuration.timeDuration, but on relogin that field means
        // "remaining minutes", not full duration — recomputing from it
        // discarded the backend's already-correct values and shrank the
        // window, wrongly zeroing out sections below. Backend handles
        // relogin/consumed-time correctly, so use its values as-is.
        const currentDate = new Date(formattedCurrentTimestamp);

        if (decodedResult?.configuration?.startExamBetweenSpecifiedTime) {
          if (decodedResult?.user?.scribeRequired) {
            const totalMinutes = decodedResult?.configuration?.sections.reduce(
              (total, section) => {
                const [hours, minutes] = section.sectionTime
                  .split(":")
                  .map(Number);
                return total + (hours * 60 + minutes);
              },
              0
            );

            const [scribeHours, scribeMinutes] =
              decodedResult.configuration?.timeDurationForScribe
                .split(":")
                .map(Number);
            const timeDurationForScribeInMinutes =
              scribeHours * 60 + scribeMinutes;

            if (totalMinutes === timeDurationForScribeInMinutes) {
              let newEndTime = new Date(
                currentDate.getTime() + totalMinutes * 60 * 1000
              );
              decodedResult.configuration.endTime = newEndTime.toISOString();
            } else {
              const differenceInMinutes =
                timeDurationForScribeInMinutes - totalMinutes;
              const sections = decodedResult.configuration.sections;
              const extraPerSection = Math.floor(
                differenceInMinutes / sections.length
              );
              const remainder = differenceInMinutes % sections.length;

              sections.forEach((section, index) => {
                let [hours, minutes] = section.sectionTime
                  .split(":")
                  .map(Number);
                let extraMinutes =
                  extraPerSection + (index < remainder ? 1 : 0);
                let totalSectionMinutes = hours * 60 + minutes + extraMinutes;
                let newHours = Math.floor(totalSectionMinutes / 60);
                let newMinutes = totalSectionMinutes % 60;
                section.sectionTime = `${String(newHours).padStart(
                  2,
                  "0"
                )}:${String(newMinutes).padStart(2, "0")}`;
              });

              const newTotalMinutes = sections.reduce((total, section) => {
                const [h, m] = section.sectionTime.split(":").map(Number);
                return total + h * 60 + m;
              }, 0);

              let newEndTime = new Date(
                currentDate.getTime() + newTotalMinutes * 60 * 1000
              );
              decodedResult.configuration.endTime = newEndTime.toISOString();
            }
          }
        }

        // Runs unconditionally so sections stay correct even if no block
        // above touched startTime/endTime.
        decodedResult.configuration.sections = reconcileSectionsToWindow(
          decodedResult?.configuration?.sections,
          decodedResult.configuration.startTime,
          decodedResult.configuration.endTime
        );

        const updatedEncodedResult = { ...decodedResult };
        const encodedResponse = yield call(() =>
          handleEncodeJson({ encodeJSON: updatedEncodedResult })
        );
        // Cookies.set("test_engine_token", decodedResult?.token?.accessToken, {
        //   expires: 7,
        // });
        // setAuthToken(decodedResult?.token?.accessToken);
        sessionStorage.setItem(
          "candidate_exam_details",
          JSON.stringify(encodedResponse)
        );
        sessionStorage.setItem(
          "candidate_details",
          JSON.stringify(decodedResult?.user || decodedResult?.candidate)
        );
      } else {
        if (decodedResult?.user?.scribeRequired) {
          const totalMinutes = decodedResult?.configuration?.sections.reduce(
            (total, section) => {
              const [hours, minutes] = section.sectionTime
                .split(":")
                .map(Number);
              return total + (hours * 60 + minutes);
            },
            0
          );

          const [scribeHours, scribeMinutes] =
            decodedResult.configuration?.timeDurationForScribe
              .split(":")
              .map(Number);
          const timeDurationForScribeInMinutes =
            scribeHours * 60 + scribeMinutes;

          if (totalMinutes != timeDurationForScribeInMinutes) {
            const differenceInMinutes =
              timeDurationForScribeInMinutes - totalMinutes;
            const sections = decodedResult.configuration.sections;
            const extraPerSection = Math.floor(
              differenceInMinutes / sections.length
            );
            const remainder = differenceInMinutes % sections.length;

            sections.forEach((section, index) => {
              let [hours, minutes] = section.sectionTime.split(":").map(Number);
              let extraMinutes = extraPerSection + (index < remainder ? 1 : 0);
              let totalSectionMinutes = hours * 60 + minutes + extraMinutes;
              let newHours = Math.floor(totalSectionMinutes / 60);
              let newMinutes = totalSectionMinutes % 60;
              section.sectionTime = `${String(newHours).padStart(
                2,
                "0"
              )}:${String(newMinutes).padStart(2, "0")}`;
            });
            decodedResult.configuration.sections = sections;
            const updatedEncodedResult = { ...decodedResult };
            const encodedResponse = yield call(() =>
              handleEncodeJson({ encodeJSON: updatedEncodedResult })
            );
            Cookies.set(
              "test_engine_token",
              decodedResult?.token?.accessToken,
              { expires: 7 }
            );
            sessionStorage.setItem(
              "candidate_details",
              JSON.stringify(decodedResult?.user || decodedResult?.candidate)
            );
            setAuthToken(decodedResult?.token?.accessToken);
            sessionStorage.setItem(
              "candidate_exam_details",
              JSON.stringify(encodedResponse)
            );
          }
        } else {
          Cookies.set("test_engine_token", decodedResult?.token?.accessToken, {
            expires: 7,
          });
          sessionStorage.setItem(
            "candidate_details",
            JSON.stringify(decodedResult?.user || decodedResult?.candidate)
          );
          setAuthToken(decodedResult?.token?.accessToken);
          if (mockMode) {
            sessionStorage.setItem("candidate_exam_details", encodedResult);
          } else {
            // forcedLogout exams need reconciling too — sections here are
            // still the untouched, original config.
            decodedResult.configuration.sections = reconcileSectionsToWindow(
              decodedResult?.configuration?.sections,
              decodedResult.configuration.startTime,
              decodedResult.configuration.endTime
            );
            const updatedEncodedResult = { ...decodedResult };
            const encodedResponse = yield call(() =>
              handleEncodeJson({ encodeJSON: updatedEncodedResult })
            );
            sessionStorage.setItem(
              "candidate_exam_details",
              JSON.stringify(encodedResponse)
            );
          }
        }
      }


      if (mockMode) {
          navigate(`/home`);
          window.location.reload();
      } else {
        // navigate(`/${Cookies.get("test_engine_project")}/home`);
        // navigate('/upload-instructions');
        console.log(proctoringType)
        if(candidateAuthorizationEnabled){
          // Navigation to /capture-identity is handled by rptCandidateLogin
          // once the RPT login response (candidateInfo) is stored in
          // sessionStorage. Navigating here would race ahead of that and
          // send the candidate to /capture-identity before candidateInfo
        }else{
          navigate('/home');
        }
        //

      }

      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) {
        document.documentElement.msRequestFullscreen();
      }

      yield put({
        type: commonActions.SET_ALERT,
        payload: {
          status: "success",
          show: true,
          message: "Exam Started Successfully",
        },
      });
    } else {
      yield put({
        type: commonActions.SET_ALERT,
        payload: {
          status: "failed",
          show: true,
          message: "Username or Password Wrong",
        },
      });
      
    }

    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
  } catch (err) {
    if (err?.response?.data?.code === "E_NOT_FOUND") {
      setError("password", {
        type: "manual",
        message: "Username or Password Wrong",
      });
    }
    yield put({
      type: commonActions.SET_ALERT,
      payload: {
        status: "failed",
        show: true,
        message: err?.response?.data?.message
          // err?.response?.data?.code === "EXAM_NOT_STARTED"
          //   ? "You can login only after the permitted login time"
          //   : err?.response?.data?.code === "LOGIN_TIME_EXPIRES"
          //     ? "Your Exam Login Time Has Ended"
          //     : err?.response?.data?.code === "E_NOT_FOUND"
          //       ? "Username or Password Incorrect"
          //       : err?.response?.data?.code === "MAXIMUM_LOGIN_ATTEMPTS_EXCEEDED"
          //         ? "Maximum login attempts exceeded"
          //         : err?.response?.data?.code === "UNPROCESSABLE_ENTITY"
          //           ? "User is already logged in"
          //           : "The Exam Has Ended or Could Not Be Found",
      },
    });
    yield delay(2000);
    yield put({
      type: commonActions.SET_ALERT,
      payload: { status: null, show: false, message: null },
    });
      yield put({
      type: commonActions.SET_BUTTON_LOADER,
      payload: false,
    });
    console.log(err);
  }
  finally{
      yield put({
      type: commonActions.SET_BUTTON_LOADER,
      payload: false,
    });
  }

};


// const candidateLogin = function* (data) {
//   const { payload } = data;
//   const { navigate } = payload;
//   navigate('/upload-instructions');
// }
export default authSagas;
